import { create } from 'zustand';

/**
 * Where a page the agent is authoring has got to.
 *
 * - `writing`: the model is still streaming the `create_artifact` /
 *   `update_artifact` call — for a new page that is the whole HTML document,
 *   which is most of the wait.
 * - `saving`: the call is complete and the server is storing it; the card the
 *   agent posts next has not arrived yet.
 */
export type ArtifactDraftPhase = 'writing' | 'saving';

/** A new page or a new version of an existing one. */
export type ArtifactDraftMode = 'create' | 'update';

export interface ArtifactDraft {
  /** The tool call writing the page; keys the pending slot so each draft opens once. */
  toolCallId: string;
  mode: ArtifactDraftMode;
  /** The page's title, once enough of the call has streamed to read it. */
  title: string | null;
  phase: ArtifactDraftPhase;
  /**
   * Set when the agent's real artifact card has landed: the slot that card
   * opens. The pending card stands aside once that slot is registered, so the
   * panel moves straight from the splash to the page.
   */
  handoffSlotId: string | null;
}

interface ArtifactDraftState {
  drafts: Record<string, ArtifactDraft>;
  start: (sessionKey: string, toolCallId: string, mode: ArtifactDraftMode) => void;
  setTitle: (sessionKey: string, toolCallId: string, title: string) => void;
  markSaving: (sessionKey: string) => void;
  handOff: (sessionKey: string, slotId: string) => void;
  clear: (sessionKey: string) => void;
}

/**
 * The page the agent is writing right now, per chat session.
 *
 * Lives outside the message list on purpose: a draft spans two streams — the
 * one that writes the page and the one, after the tool runs, that posts its
 * card — and block indices are reset between them.
 */
export const useArtifactDraftStore = create<ArtifactDraftState>((set) => ({
  drafts: {},
  start: (sessionKey, toolCallId, mode) =>
    set((state) => ({
      drafts: {
        ...state.drafts,
        [sessionKey]: { toolCallId, mode, title: null, phase: 'writing', handoffSlotId: null },
      },
    })),
  setTitle: (sessionKey, toolCallId, title) =>
    set((state) => {
      const draft = state.drafts[sessionKey];
      if (!draft || draft.toolCallId !== toolCallId || draft.title === title) return state;
      return { drafts: { ...state.drafts, [sessionKey]: { ...draft, title } } };
    }),
  markSaving: (sessionKey) =>
    set((state) => {
      const draft = state.drafts[sessionKey];
      if (!draft || draft.phase === 'saving') return state;
      return { drafts: { ...state.drafts, [sessionKey]: { ...draft, phase: 'saving' } } };
    }),
  handOff: (sessionKey, slotId) =>
    set((state) => {
      const draft = state.drafts[sessionKey];
      if (!draft || draft.handoffSlotId === slotId) return state;
      return { drafts: { ...state.drafts, [sessionKey]: { ...draft, handoffSlotId: slotId } } };
    }),
  // Every stream releases on done/error whether or not it wrote a page, so a
  // clear with nothing to clear must leave the state untouched.
  clear: (sessionKey) =>
    set((state) => {
      if (!(sessionKey in state.drafts)) return state;
      const drafts = { ...state.drafts };
      delete drafts[sessionKey];
      return { drafts };
    }),
}));
